import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import ROUTES from "../../../../AppRouter/routes";
import Box from "@mui/material/Box";
import SuperUserPanel from "./SuperUserPanel";
import UpdateOrganizationDialog from "./UpdateOrganizationDialog";
import FiltersDialog from "./FiltersDialog";
import { useWebServer } from "../../../../../providers";
import {
  fetchAllUsers,
  fetchOrganizations,
  selectSuperUserOrg,
  createOrganization,
  updateOrganization,
  fetchAlgVectorTypes,
} from "../../../../../actions";
import AccessDeniedPanel from "./AccessDenied";
import SuperUserFilterHeader from "./FilterHeader";

const Index = ({
  organizations,
  superUser,
  orgDevices,
  orglessUsers,
  userCounts,
  orgFilter = "",
  deviceFilter = "",
  userFilter = "",
  fetchAllUsers,
  fetchOrganizations,
  selectSuperUserOrg,
  createOrganization,
  updateOrganization,
  fetchAlgVectorTypes,
}) => {
  const history = useHistory();
  const { sendRequest, sendOrganizationRequest } = useWebServer();
  const [orgs, setOrgs] = useState([]);
  const [displayCreateOrg, setDisplayCreateOrg] = useState(false);
  const [displayUpdateOrg, setDisplayUpdateOrg] = useState(null);
  const [displaySearchFilters, setDisplaySearchFilters] = useState(false);

  useEffect(() => {
    fetchAlgVectorTypes(sendRequest);
  }, [sendRequest, fetchAlgVectorTypes]);

  // Both are needed here, not just the users: the organization list is what
  // decides which users count as unaffiliated, so without it every user would be
  // bucketed as having a deleted organization. It also supplies the device
  // counts. Fetched here rather than relying on a sibling panel to have done it.
  useEffect(() => {
    fetchAllUsers(sendRequest);
    fetchOrganizations(sendOrganizationRequest);
  }, [sendRequest, sendOrganizationRequest, fetchAllUsers, fetchOrganizations]);

  useEffect(() => {
    setOrgs(
      Object.entries(orgDevices)
        .reduce((arr, [org, tools]) => {
          if (org.includes(orgFilter)) {
            const deviceCount = (tools || []).filter((tool) =>
              tool.uuid.includes(deviceFilter)
            ).length;
            // When a device UUID filter is active, hide orgs with no
            // matching devices. With no filter, show all orgs as normal.
            if (deviceFilter !== "" && deviceCount === 0) {
              return arr;
            }
            return [
              ...arr,
              {
                org,
                devices: deviceCount,
                users: userCounts[org] || 0,
              },
            ];
          }
          return arr;
        }, [])
        .sort((a, b) => {
          if (a.org < b.org) {
            return -1;
          }
          if (a.org > b.org) {
            return 1;
          }
          return 0;
        })
    );
  }, [setOrgs, orgDevices, userCounts, orgFilter, deviceFilter, userFilter]);

  const handleSelectGroup = (org, group) => {
    let nextSelected = `${org};${group}`;
    if (org === null || group === null) {
      nextSelected = "";
    }
    selectSuperUserOrg(nextSelected);
  };

  const handleAddOrg = (orgInfo) => {
    const { uuid, name, address, gps, vectorTypes, address_details } = orgInfo;
    createOrganization(sendRequest, uuid, address, gps, name, vectorTypes, address_details);
  };

  const handleUpdateOrg = (orgInfo) => {
    const { uuid, name, address, gps, vectorTypes, address_details } = orgInfo;
    updateOrganization(sendRequest, uuid, address, gps, name, vectorTypes, address_details);
  };

  const handleClose = () => {
    history.push(ROUTES.app);
  };

  return superUser ? (
    <Box>
      <UpdateOrganizationDialog
        isActive={displayCreateOrg}
        organizations={organizations}
        setIsActive={setDisplayCreateOrg}
        onConfirm={handleAddOrg}
      />
      <UpdateOrganizationDialog
        isActive={displayUpdateOrg}
        organizations={organizations}
        setIsActive={setDisplayUpdateOrg}
        onConfirm={handleUpdateOrg}
        selectedOrg={displayUpdateOrg}
      />
      <FiltersDialog
        isActive={displaySearchFilters}
        setIsActive={setDisplaySearchFilters}
      />
      <SuperUserFilterHeader
        setDisplayCreateOrg={setDisplayCreateOrg}
        setDisplaySearchFilters={setDisplaySearchFilters}
        handleClose={handleClose}
      />

      <SuperUserPanel
        orgs={orgs}
        orglessUsers={orglessUsers}
        handleSelectGroup={handleSelectGroup}
        handleUpdateOrg={(org) => setDisplayUpdateOrg(org)}
      />
    </Box>
  ) : (
    <AccessDeniedPanel />
  );
};

export const mapStateToProps = (state) => {
  const organizations = state?.user?.organizations || {};
  const superUser = state?.user?.superUser || false;
  const orgDevices = state?.user?.orgDevices || {};
  const allUsers = state?.user?.allUsers || [];

  // Counted by IDX MEMBERSHIP, matching how the user list itself buckets.
  // Membership is what grants access; the primary organization is only a
  // default, and someone can hold IDX access to an organization that is not
  // theirs.
  //
  // These counts therefore do NOT sum to the number of users: a user in several
  // organizations is counted under each, which is the honest answer to "how many
  // people can see this organization".
  //
  // "Unaffiliated" is "no IDX access" - no membership for any organization IDX
  // has - which covers both a user never granted one and a user whose
  // organization was since deleted.
  const known = new Set(Object.keys(organizations));
  const userCounts = {};
  let orglessUsers = 0;
  allUsers.forEach((user) => {
    // memberships arrive already filtered to IDX by the user service.
    const idxOrgs = (user?.memberships || []).filter((m) => known.has(m.org));
    if (idxOrgs.length === 0) {
      orglessUsers += 1;
      return;
    }
    idxOrgs.forEach((m) => {
      userCounts[m.org] = (userCounts[m.org] || 0) + 1;
    });
  });
  const { orgFilter, deviceFilter, userFilter } = state?.superuser;
  return {
    organizations,
    superUser,
    orgDevices,
    orglessUsers,
    userCounts,
    orgFilter,
    deviceFilter,
    userFilter,
  };
};

export default connect(mapStateToProps, {
  fetchAllUsers,
  fetchOrganizations,
  selectSuperUserOrg,
  createOrganization,
  updateOrganization,
  fetchAlgVectorTypes,
})(Index);
